// Modules
import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import moment from 'moment';
import store from 'app-store';
import { useQuery, useQueryClient } from 'react-query';
import { useSocket } from "services/socket";
import { useSessionStorage } from 'usehooks-ts';
import _ from 'lodash';

// Action types
import { UPDATE_TRIAGE_ITEMS, SET_TRIAGE_USERS, SET_OPTION_MENU_OPEN } from './action-types';

// Services
import Triage from 'services/data/triage';

// Hooks
import { useGatedCallback } from 'hooks';

// Utils
import newTriageAlert from "utils/new-triage-alert";

// The shared react-query key for the cached triage list. All four mounts of
// useTriageItems read and write this one entry, which is what makes a single
// fetch serve them all. Writes that come from acting on an item go through
// hooks/useTriageMutation, which finds this list by key rather than importing it.
const TRIAGE_KEY = ['triage'];

/**
 * The queue container, and so also the key of the gate that holds updates back
 * (see hooks/useGatedCallback). Anything that renders the queue has to carry the
 * matching `data-triage-queue` attribute for interactions inside it to count, and
 * anything that wants to show the wait keys off this same selector.
 */
export const TRIAGE_QUEUE_SELECTOR = '[data-triage-queue]';

/** Quiet period before a held update is applied. */
export const TRIAGE_HOLD_MS = 5000;

// Stable identity, so the memo below does not see a new array on every render
// before the first fetch resolves.
const NO_ITEMS = [];


// Action creators
export function update(items) {
  store.dispatch({ type: UPDATE_TRIAGE_ITEMS, items });
}

export function openForm(triageItem, optionMenuOpen) {
  store.dispatch({ type: SET_OPTION_MENU_OPEN, itemIdentifier: `${triageItem.id}-${triageItem.type}`, optionMenuOpen });
}

export function setUsers(users) {
  store.dispatch({ type: SET_TRIAGE_USERS, users });
}


function useInterval(callback, delay) {
  const savedCallback = useRef();

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    function tick() {
      savedCallback.current();
    }
    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}

/**
 * @param socket  Subscribe to version messages and drive freshness. Off by
 *                default: the mounts that only want `team`/`setTeam` or a read of
 *                the shared cache have no business holding a subscription, and
 *                four mounts each holding one meant four refetches per change.
 *                Turned on by whichever component actually renders the queue.
 */
export function useTriageItems({ user, socket = false }) {
  const [team, setTeam] = useSessionStorage('triage_team', null);
  const [criticalTriageItems, setCriticalTriageItems] = useState([]);
  const [cachedFilteredTriageItems, setCachedFilteredTriageItems] = useState([]);
  const queryClient = useQueryClient();

  /**
   * Fetches the cached list, with a monotonic guard.
   *
   * The guard is cheap insurance rather than a load-bearing part of the design —
   * the server serializes versions and the fetch always returns current data —
   * but two refetches can still resolve out of order, and rendering an older
   * list than the one already on screen is exactly the jumpiness being fixed.
   */
  const fetchList = useCallback(async () => {
    const fetched = await Triage.findCached();
    const current = queryClient.getQueryData(TRIAGE_KEY);

    // Only compare when both sides are versioned. A null version means the server
    // had nothing cached and queried directly, so that response is the freshest
    // thing available and must not be discarded.
    const comparable =
      current && typeof current.version === 'number' && typeof fetched.version === 'number';

    if (comparable && fetched.version < current.version) return current;

    const sorted = _.orderBy(fetched.items, [Triage.getSortKey], ["desc"])
    return { ...fetched, items: sorted };
  }, [queryClient]);

  const { data: cached, ...queryData } = useQuery(TRIAGE_KEY, fetchList, {
    // The socket drives freshness — there is no polling and nothing expires on a
    // timer.
    cacheTime: Infinity,
    staleTime: Infinity,

    /**
     * ...with one exception that matters. While no component is mounted the
     * socket is deregistered (services/socket.js drops the listener when the
     * last subscriber unmounts), so any version published in the meantime is
     * missed. `staleTime: Infinity` means the default `refetchOnMount: true`
     * would consider the cached list fresh forever and never refetch, so
     * navigating away and back would show an arbitrarily old queue.
     *
     * 'always' forces one fetch per mount. It is close to free: unchanged data
     * costs a 304 with no body, which is exactly what the ETag is for.
     */
    refetchOnMount: 'always',

    // Both reconnect paths are already covered by the socket: io.socket's
    // 'reconnect' event and the visibilitychange handler in services/socket.js
    // both run reconnectAllSockets, which fires onReconnect below.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });

  // Undefined until the first fetch resolves — the query no longer seeds itself
  // with an empty array the way the socket-only version did.
  const triageItems = cached?.items ?? NO_ITEMS;

  /**
   * Applying a version reorders the queue, so it waits for a pause in whatever the
   * user is doing rather than landing under their cursor. Immediate when the queue
   * is idle, held until it goes quiet when it is not.
   *
   * Tied to `socket` because that is what makes this mount the one that applies
   * anything — a null selector leaves the gate inert rather than registering a
   * caller that never calls. Mounts that do subscribe share one gate, since they
   * share the selector, so they also share one hold and one refetch.
   */
  const { call: applyVersion } = useGatedCallback(
    () => queryClient.refetchQueries(TRIAGE_KEY),
    { selector: socket ? TRIAGE_QUEUE_SELECTOR : null, holdMs: TRIAGE_HOLD_MS }
  );

  // A new version exists.
  const onVersion = useCallback(message => {
    const version = Number(message?.version ?? 0);
    const current = queryClient.getQueryData(TRIAGE_KEY)?.version ?? 0;

    if (version <= current) return;

    applyVersion();
  }, [queryClient, applyVersion]);

  // On reconnect there is no way to know which versions were missed while the
  // socket was down, so refetch unconditionally.
  const onReconnect = useCallback(() => {
    queryClient.refetchQueries(TRIAGE_KEY);
  }, [queryClient]);

  useSocket('triageVersion', onVersion, onReconnect, socket);

  const checkCriticalTriageItems = () => {
    const currentCrits = triageItems.filter((item) => (item.status === 'Active' && item.claimedAt === null && Math.abs(moment(item.createdAt).diff(moment(), 'seconds')) >= 60)).map(item => item.id);
    
    if (!_.isEqual(criticalTriageItems, currentCrits)) setCriticalTriageItems(currentCrits);
  };

  useInterval(checkCriticalTriageItems, 1000);

  const filteredTriageItems = useMemo(() => {
    const currentTime = moment();
    const filtered = triageItems
      ? triageItems.filter((item) => {
        if (
            team &&
            item.company &&
            item.company.team &&
            item.company.team.id !== team.id &&
            (!item.claimedBy || (item.claimedBy && item.claimedBy.id !== user.id) || (item.claimedBy && item.claimedBy.team && item.claimedBy.team.id !== team.id)) &&
            !(item.status === 'Active' && item.claimedAt === null && Math.abs(moment(item.createdAt).diff(currentTime, 'seconds')) >= 60)
          ) {
          return false;
        }
        return true;
      })
      : [];
    return (filtered);
  }, [team, triageItems, criticalTriageItems, user]);

  useEffect(() => {
    newTriageAlert(cachedFilteredTriageItems, filteredTriageItems);
    setCachedFilteredTriageItems(filteredTriageItems);
  }, [filteredTriageItems]);

  return { ...queryData, items: filteredTriageItems, team, setTeam };
}
